import React, { useMemo, useState } from 'react';
import { loadStripe, Stripe, StripeElementsOptions, StripeElementsOptionsMode } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { cn } from '@utils/index';
import { createServerSetupIntent, initializePayment } from 'src/services/profile';
import { useAuthStore } from 'src/store/auth';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import { useFormControls } from '@features/onboarding/hooks/useFormControls';
import { usePaymentStore } from '../../store/payment';
import { AnimatedDots } from '@components/shared/animated-dots/AnimatedDots';
import { useApplicationStore } from 'src/store/application';
import { useFinalSubmissionStore } from 'src/store/final-submission';

// Publishable key from environment variables
const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_APP_STRIPE_PUBLISHABLE_KEY || '';
const stripePromise: Promise<Stripe | null> = loadStripe(STRIPE_PUBLISHABLE_KEY);

export const StripeBanksElementForm: React.FC<{
  setCurrentStep: (step: 'connect' | 'submit') => void;
  isHealthhub?: boolean;
  onPaymentMethodSet?: (paymentMethodId: string) => void;
  isSubmitting?: boolean;
}> = ({ setCurrentStep, isHealthhub = false, onPaymentMethodSet, isSubmitting = false }) => {
  const { user } = useAuthStore();
  const { currentApplicationId } = useApplicationStore();
  const { isSubmitted } = useFinalSubmissionStore();
  const { isPaymentInitialized, setPaymentInitialized } = usePaymentStore();
  const [error, setError] = useState<string | null>(null);

  const formControls = useFormControls();
  const setIsLocalLoading = formControls?.setIsLocalLoading;

  console.log('user', user)

  const updatedFormData = useOnboardingStore.getState().data;
  const primaryApplicant = updatedFormData.applicants?.find((e) => e.applicant === 'primary');
  const [loading, setLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [serverSetupId, setServerSetupId] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const initializationStartedRef = React.useRef(false);
  const applicantFullName = useMemo(() => {
    const first = (primaryApplicant as any)?.firstName || '';
    const last = (primaryApplicant as any)?.lastName || '';
    return [first, last].filter(Boolean).join(' ').trim() || undefined;
  }, [primaryApplicant]);

  if (!STRIPE_PUBLISHABLE_KEY) {
    return <p className="text-error">Stripe publishable key is missing.</p>;
  }

  // Initialize SetupIntent on mount — once per mount, skip if payment already done
  React.useEffect(() => {
    if (isSubmitted) return;
    if (usePaymentStore.getState().paymentMethodId) return;
    if (initializationStartedRef.current) return;

    initializationStartedRef.current = true;

    let mounted = true;

    const initPayment = isHealthhub
      ? createServerSetupIntent
      : currentApplicationId
        ? () => initializePayment(currentApplicationId)
        : null;

    if (!initPayment) {
      console.warn('[StripeBanksElementForm] No context to initialize payment');
      initializationStartedRef.current = false;
      return;
    }

    setIsInitializing(true);
    setIsLocalLoading?.(true);

    initPayment()
      .then(({ id, secret }) => {
        if (!mounted) return;
        setServerSetupId(id);
        setClientSecret(secret);
        setPaymentInitialized(true);
      })
      .catch((e) => {
        console.error('[StripeBanksElementForm] Failed to initialize payment:', e);
        setError(e?.message || 'Failed to initialize payment');
        initializationStartedRef.current = false;
      })
      .finally(() => {
        setIsInitializing(false);
        setIsLocalLoading?.(false);
      });

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHealthhub, currentApplicationId, isSubmitted]);

  const appearance = useMemo(() => ({ theme: 'stripe' as const }), []);

  const options: StripeElementsOptions | StripeElementsOptionsMode = clientSecret
    ? ({ clientSecret, appearance, loader: 'auto' } as StripeElementsOptions)
    : ({
      mode: 'payment',
      currency: 'usd',
      amount: 1000,
      paymentMethodTypes: ['us_bank_account'],
      setupFutureUsage: 'off_session',
      captureMethod: 'automatic',
      appearance,
    } as StripeElementsOptionsMode);

  if (!clientSecret || isInitializing) {
    return <p className="text-sm text-muted-foreground">
      Setting up secure payment…<br />
      <AnimatedDots />
    </p>;
  }

  return (
    <Elements stripe={stripePromise} options={options}>
      <PaymentElementInner
        setError={setError}
        setLoading={setLoading}
        loading={loading}
        userEmail={user?.email}
        userName={applicantFullName}
        clientSecret={clientSecret}
        serverSetupId={serverSetupId}
        setCurrentStep={setCurrentStep}
        onPaymentMethodSet={onPaymentMethodSet}
        isSubmitting={isSubmitting}
      />
    </Elements>
  );
};

const PaymentElementInner: React.FC<{
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  userEmail?: string;
  userName?: string;
  loading?: boolean;
  clientSecret?: string | null;
  serverSetupId?: string | null;
  setCurrentStep: (step: 'connect' | 'submit') => void;
  onPaymentMethodSet?: (paymentMethodId: string) => void;
  isSubmitting?: boolean;
}
> = ({ setError, setLoading, userEmail, userName, loading, clientSecret, serverSetupId, setCurrentStep, onPaymentMethodSet, isSubmitting = false }) => {
  const { setPaymentMethodId } = usePaymentStore();
  const stripe = useStripe();
  const elements = useElements();
  const [message, setMessage] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isPaymentConfirmed, setIsPaymentConfirmed] = useState(false);

  const formControls = useFormControls();
  const isLocalLoading = formControls?.isLocalLoading;
  const handleSubmit = async (e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault();
    console.log('[StripeBanksElementForm] handleSubmit called', {
      loading,
      isSuccess,
      isSubmitting,
      hasStripe: !!stripe,
      hasElements: !!elements,
      hasClientSecret: !!clientSecret,
      hasOnPaymentMethodSet: !!onPaymentMethodSet,
    });

    if (loading || isSuccess || isSubmitting) {
      console.log('[StripeBanksElementForm] Submit blocked: already loading or success or submitting', { loading, isSuccess, isSubmitting });
      return;
    }

    if (!stripe || !elements || !clientSecret) {
      console.error('[StripeBanksElementForm] Stripe not initialized', {
        hasStripe: !!stripe,
        hasElements: !!elements,
        hasClientSecret: !!clientSecret,
      });
      setMessage('Stripe is not initialized');
      return;
    }

    console.log('[StripeBanksElementForm] Starting form submission');
    setMessage(null);
    setLoading(true);

    try {
      const billingDetails: { name?: string; email?: string } = {};
      if (userName) billingDetails.name = userName;
      if (userEmail) {
        billingDetails.email = userEmail;
      }
      console.log('[StripeBanksElementForm] Billing details prepared', { billingDetails });

      // 1. Сначала валидируем форму через elements.submit()
      console.log('[StripeBanksElementForm] Validating form via elements.submit()');
      const { error: submitError } = await elements.submit();
      if (submitError) {
        console.error('[StripeBanksElementForm] Form validation failed:', submitError);
        setMessage(submitError.message ?? 'Payment form validation failed');
        setError(submitError.message ?? 'Payment form validation failed');
        return;
      }
      console.log('[StripeBanksElementForm] Form validation passed');

      // 2. Затем подтверждаем SetupIntent
      console.log('[StripeBanksElementForm] Confirming SetupIntent', {
        hasClientSecret: !!clientSecret,
        billingDetails,
      });
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        clientSecret,
        confirmParams: {
          return_url: window.location.href,
          payment_method_data: {
            billing_details: billingDetails,
          },
        },
        redirect: 'if_required',
      });

      if (error || !setupIntent) {
        console.error('[StripeBanksElementForm] SetupIntent confirmation failed:', {
          error,
          hasSetupIntent: !!setupIntent,
        });
        setMessage(error?.message ?? 'Payment setup failed');
        setError(error?.message ?? 'Payment setup failed');
        return;
      }

      console.log('[StripeBanksElementForm] SetupIntent confirmed successfully', {
        setupIntentId: setupIntent.id,
        status: setupIntent.status,
        paymentMethod: setupIntent.payment_method,
      });

      const paymentMethodId = (setupIntent.payment_method || '') as string;
      if (!paymentMethodId) {
        console.error('[StripeBanksElementForm] Payment method ID not returned from SetupIntent');
        setMessage('Payment method was not returned');
        setError('Payment method was not returned');
        return;
      }

      console.log('[StripeBanksElementForm] Payment method ID received:', paymentMethodId);
      setMessage('Payment method saved successfully!');
      setIsSuccess(true);
      setPaymentMethodId(paymentMethodId);
      console.log('[StripeBanksElementForm] Payment method ID saved to store');
      
      // Вызываем callback, если он передан (для BillingAccounts)
      if (onPaymentMethodSet) {
        console.log('[StripeBanksElementForm] Calling onPaymentMethodSet callback', { paymentMethodId });
        onPaymentMethodSet(paymentMethodId);
        console.log('[StripeBanksElementForm] onPaymentMethodSet callback completed');
      } else {
        console.log('[StripeBanksElementForm] No onPaymentMethodSet callback provided');
      }
    } catch (error: unknown) {
      console.error('[StripeBanksElementForm] Unexpected error during form submission:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      setMessage(errorMessage);
      setError(errorMessage);
    } finally {
      console.log('[StripeBanksElementForm] Form submission finished, setting loading to false');
      setLoading(false);
    }
  };

  return (
    <form id="stripeForm" onSubmit={handleSubmit} className="space-y-4 max-w-md w-full mx-auto">
      <PaymentElement
        onChange={(event) => {
          console.log('[StripeBanksElementForm] PaymentElement onChange event', {
            complete: event.complete,
            empty: event.empty,
            value: event.value,
          });
          if (event.complete) {
            console.log('[StripeBanksElementForm] Payment element completed, setting payment confirmed and step to submit');
            setIsPaymentConfirmed(true);
            setCurrentStep('submit');
          }
        }}
        id="payment-element"
        options={{
          layout: { type: 'accordion', defaultCollapsed: false },
          paymentMethodOrder: ['us_bank_account'],
          fields: {
            billingDetails: {
              email: userEmail ? 'never' : 'auto',
              name: userName ? 'never' : 'auto',
            },
          },
          defaultValues: userEmail || userName ? { billingDetails: { email: userEmail, name: userName } } : undefined,
        }}
      />
      {message && message !== 'No account connected' && !isSuccess && (
        <p className={cn('text-sm', isSuccess ? 'text-green-500' : 'text-red-500')}>
          {message}
        </p>
      )}

      {/* Показываем кнопку только если банк еще не подключен */}
      {isPaymentConfirmed && (
        <div className="w-fit m-auto max-w-2xl">
          <div className="mt-6 text-center">
            <button
              type="submit"
              className={cn(
                "bg-primary hover:bg-primary/90 text-white font-semibold py-3 px-8 rounded-lg transition-colors duration-200",
                loading || isSuccess || isSubmitting ? "pointer-events-none opacity-75" : ""
              )}
              disabled={loading || isSuccess || isSubmitting}
            >
              {!isSuccess && !isSubmitting && <span className={(loading ? "opacity-75" : "")}>
                {!loading ? 'SUBMIT' : 'Processing...'}
              </span>}
              {(isSuccess || isSubmitting) && <AnimatedDots />}
            </button>
          </div>
        </div>
      )}
    </form>
  );
};
